import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spin, message } from 'antd';
import { BarChartOutlined, FileExcelOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney, formatNumberTR } from '../../utils/currency';
import * as XLSX from 'xlsx';
import './Borsa.css';

function formatTRY(value) {
    return formatMoney(value);
}

function formatUSD(value) {
    return formatMoney(value, 'USD');
}

function formatCurrency(value, currency) {
    if (value == null || Number.isNaN(value)) value = 0;
    return formatMoney(value, currency);
}

function generateFileName() {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}-gunluk.xlsx`;
}

const BorsaGunluk = () => {
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [hisseler, setHisseler] = useState([]);
    const [guncelDolar, setGuncelDolar] = useState(1);
    const [bist100, setBist100] = useState('');
    const [bistYuzde, setBistYuzde] = useState('');
    const [bistYuzdeFlash, setBistYuzdeFlash] = useState(null);
    const [fark, setFark] = useState({
        fark_maliyet: 0,
        fark_tutar: 0,
        fark_karzarar: 0,
        fark_dolar_maliyet: 0,
        fark_dolar_tutar: 0,
        fark_dolar_karzarar: 0,
    });
    const [farkRowFlash, setFarkRowFlash] = useState(null);

    const fetchGunluk = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/borsa/hisseler_gunluk`);
            if (!res.ok) throw new Error(res.statusText);
            const data = await res.json();
            setHisseler(data.hisseler || []);
            setGuncelDolar(Number(data.guncel_dolar) || 1);
            setBist100(data.bist100 != null ? String(data.bist100) : '');
            const yuzde = data.bist_yuzde != null ? Number(data.bist_yuzde) : 0;
            const farkVal = data.bist_fark != null ? Number(data.bist_fark) : 0;
            setBistYuzde(`% ${formatNumberTR(yuzde)} (${formatNumberTR(farkVal)})`);
            setBistYuzdeFlash(yuzde > 0 ? 'greenflash' : yuzde < 0 ? 'redflash' : null);
            setFark({
                fark_maliyet: data.fark_maliyet ?? 0,
                fark_tutar: data.fark_tutar ?? 0,
                fark_karzarar: data.fark_karzarar ?? 0,
                fark_dolar_maliyet: data.fark_dolar_maliyet ?? 0,
                fark_dolar_tutar: data.fark_dolar_tutar ?? 0,
                fark_dolar_karzarar: data.fark_dolar_karzarar ?? 0,
            });
            const kr = Number(data.fark_karzarar);
            setFarkRowFlash(kr > 0 ? 'greenflash' : kr < 0 ? 'redflash' : null);
        } catch (e) {
            message.error('Günlük veri yüklenemedi: ' + (e.message || e));
            setHisseler([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGunluk();
    }, [fetchGunluk]);

    useEffect(() => {
        if (bistYuzdeFlash) {
            const t = setTimeout(() => setBistYuzdeFlash(null), 1500);
            return () => clearTimeout(t);
        }
    }, [bistYuzdeFlash]);

    const downloadExcel = () => {
        const table = tableRef.current;
        if (!table) {
            message.warning('Tablo bulunamadı.');
            return;
        }
        try {
            const wb = XLSX.utils.table_to_book(table, { sheet: 'sheet1' });
            XLSX.writeFile(wb, generateFileName());
            message.success('Excel indirildi.');
        } catch (e) {
            message.error('Excel oluşturulamadı: ' + (e.message || e));
        }
    };

    const goBorsa = () => navigate('/borsa');
    const toplamFilter = (h) => h.kurum !== 'TOPLAM';
    const toplamOrtMaliyetTRY = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + (Number(h.ort_maliyet) || 0) * (Number(h.adet) || 0), 0);
    const toplamGuncelTutarTRY = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + (Number(h.guncel_fiyat) || 0) * (Number(h.adet) || 0), 0);
    const toplamKarZararTRY = toplamGuncelTutarTRY - toplamOrtMaliyetTRY;
    const toplamOrtMaliyetUSD = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + ((Number(h.ort_maliyet) || 0) / (Number(h.dolar) || 1)) * (Number(h.adet) || 0), 0);
    const toplamGuncelTutarUSD = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + ((Number(h.guncel_fiyat) || 0) / (Number(h.guncel_dolar) || 1)) * (Number(h.adet) || 0), 0);
    const toplamKarZararUSD = toplamGuncelTutarUSD - toplamOrtMaliyetUSD;

    if (loading && hisseler.length === 0) {
        return (
            <div className="borsa-wrap" style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="borsa-wrap borsa-gunluk-wrap">
            <div className="borsa-actions hide-on-print">
                <Button
                    danger
                    type="primary"
                    className="btns borsa-btn-chart"
                    icon={<BarChartOutlined />}
                    onClick={goBorsa}
                    title="Borsa"
                    style={{ marginRight: 8 }}
                />
                <Button
                    type="default"
                    className="btns borsa-btn-excel"
                    icon={<FileExcelOutlined />}
                    onClick={downloadExcel}
                    title="Excel indir"
                />
            </div>

            <div className="borsa-table-wrap">
                <table ref={tableRef} className="table borsatable btable" id="borsaData">
                    <tbody>
                        <tr>
                            <td colSpan={16}>
                                <h1>BORSA RAPORU</h1>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={1} className="BIST100">BIST100</td>
                            <td colSpan={1} className="BIST100" id="BIST100">
                                {bist100}
                            </td>
                            <td colSpan={1} className={`BISTYUZDE ${bistYuzdeFlash || ''}`} id="BISTYUZDE">
                                {bistYuzde}
                            </td>
                            <td />
                            <td colSpan={5} className="text-center">Türk Lirası</td>
                            <td colSpan={5} className="text-center">Amerikan Doları</td>
                            <td colSpan={2} className="text-center">Kurlar</td>
                        </tr>
                        <tr>
                            <td>Hisse</td>
                            <td>Adet</td>
                            <td>Yüzde</td>
                            <td>Aracı Kurum</td>
                            <td>Ort. Maliyet</td>
                            <td>Toplam Maliyet</td>
                            <td>Güncel Fiyat</td>
                            <td>Güncel Tutar</td>
                            <td>Kar/Zarar</td>
                            <td>Ort. Maliyet</td>
                            <td>Toplam Maliyet</td>
                            <td>Güncel Fiyat</td>
                            <td>Güncel Tutar</td>
                            <td>Kar/Zarar</td>
                            <td>Alış USD</td>
                            <td>Güncel USD</td>
                        </tr>
                        {hisseler.map((hisse, idx) => {
                            const isTotal = hisse.kurum === 'TOPLAM';
                            const adet = Number(hisse.adet) || 0;
                            const ortMaliyet = Number(hisse.ort_maliyet) || 0;
                            const guncelFiyat = Number(hisse.guncel_fiyat) || 0;
                            const dolar = Number(hisse.dolar) || 1;
                            const guncelDolar = Number(hisse.guncel_dolar) || 1;
                            const toplamMaliyetTRY = ortMaliyet * adet;
                            const guncelTutarTRY = guncelFiyat * adet;
                            const karZararTRY = guncelTutarTRY - toplamMaliyetTRY;
                            const ortMaliyetUSD = ortMaliyet / dolar;
                            const toplamMaliyetUSD = ortMaliyetUSD * adet;
                            const guncelFiyatUSD = guncelFiyat / guncelDolar;
                            const guncelTutarUSD = guncelFiyatUSD * adet;
                            const karZararUSD = guncelTutarUSD - toplamMaliyetUSD;
                            const rowClass = [hisse.color, isTotal ? 'total' : 'satir'].filter(Boolean).join(' ');
                            const yuzdeStr = hisse.yuzde != null ? `${hisse.yuzde} (${formatTRY(hisse.fiyat_farki)})` : '-';
                            return (
                                <tr key={`${hisse.kod}-${hisse.id}-${idx}`} className={rowClass || undefined}>
                                    <td className={hisse.color || ''} style={{ textAlign: 'left' }}>
                                        {hisse.kod}
                                    </td>
                                    <td>{hisse.adet}</td>
                                    <td>{yuzdeStr}</td>
                                    <td>{hisse.kurum}</td>
                                    <td>{formatTRY(ortMaliyet)}</td>
                                    <td>{formatTRY(toplamMaliyetTRY)}</td>
                                    <td>{formatTRY(guncelFiyat)}</td>
                                    <td>{formatTRY(guncelTutarTRY)}</td>
                                    <td>{formatTRY(karZararTRY)}</td>
                                    <td>{formatUSD(ortMaliyetUSD)}</td>
                                    <td>{formatUSD(toplamMaliyetUSD)}</td>
                                    <td>{formatUSD(guncelFiyatUSD)}</td>
                                    <td>{formatUSD(guncelTutarUSD)}</td>
                                    <td>{formatUSD(karZararUSD)}</td>
                                    <td>{formatTRY(dolar)}</td>
                                    <td>{formatTRY(guncelDolar)}</td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td colSpan={3}>Toplam</td>
                            <td />
                            <td>{formatTRY(toplamOrtMaliyetTRY)}</td>
                            <td />
                            <td>{formatTRY(toplamGuncelTutarTRY)}</td>
                            <td>{formatTRY(toplamKarZararTRY)}</td>
                            <td />
                            <td>{formatUSD(toplamOrtMaliyetUSD)}</td>
                            <td />
                            <td>{formatUSD(toplamGuncelTutarUSD)}</td>
                            <td>{formatUSD(toplamKarZararUSD)}</td>
                            <td />
                            <td />
                        </tr>
                        <tr id="farklar" className={farkRowFlash || ''}>
                            <td colSpan={3}>Düne göre fark</td>
                            <td />
                            <td id="fark_maliyet">{formatCurrency(fark.fark_maliyet, 'TRY')}</td>
                            <td />
                            <td id="fark_tutar">{formatCurrency(fark.fark_tutar, 'TRY')}</td>
                            <td id="fark_karzarar">{formatCurrency(fark.fark_karzarar, 'TRY')}</td>
                            <td />
                            <td id="fark_dolar_maliyet">{formatCurrency(fark.fark_dolar_maliyet, 'USD')}</td>
                            <td />
                            <td id="fark_dolar_tutar">{formatCurrency(fark.fark_dolar_tutar, 'USD')}</td>
                            <td id="fark_dolar_karzarar">{formatCurrency(fark.fark_dolar_karzarar, 'USD')}</td>
                            <td />
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BorsaGunluk;
